import React, { useEffect, useState } from 'react'
import { Button, Form } from 'semantic-ui-react'
import EditableLabelInput from '~/components/EditableLabelInput'
import { formatNumber, handleErrors } from '~/utils/helpers'
import { heiferPhaseTypes } from '../../../constants'
import InfoPopup from '~/components/InfoPopup'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { FormFields } from '~/components/FormFields'
import HeiferPhasesTable from './HeiferPhasesTable'
import { heiferFeedingPhases, individualPhases } from '../../../utils'
import HeiferIndividualEdit from './HeiferIndividualEdit'
import { useParams } from 'react-router-dom'
import CO2Service, { CO2OptionsType } from '~/api/services/co2'

const DAYS_IN_MONTH_LFL = 30.5

const NO_FUTTERBAU_ID = 3
interface Props {
  milkOptions: CO2OptionsType
}

const HeiferPhases: React.FC<Props> = ({ milkOptions }) => {
  const { id } = useParams()
  const { control, setValue, clearErrors } = useFormContext()

  const [isOpenIndPhasesModal, setIsOpenIndPhasesModal] = useState(false)

  const watchCalvesNumber = useWatch({ control, name: 'youngCattleNumber' })
  const watchFirstCalvingAge = useWatch({ control, name: 'firstCalvingAge' })
  const watchRearingPhasesType = useWatch({ control, name: 'rearingPhasesType' })
  const watchHeiferRaisingMethod = useWatch({ control, name: 'heiferRaisingMethodId' })
  const watchYoungCattleWeightBeforeCalving = useWatch({ control, name: 'youngCattleWeightBeforeCalving' })
  const watchGrowSilage = useWatch({ control, name: 'silageTypes' }) || []

  const { fields, replace } = useFieldArray({
    control: control,
    name: 'rearingPhases',
    keyName: 'key'
  })

  useEffect(() => {
    if (watchRearingPhasesType === 'dlg') {
      fetchDlgFeed()
    }
  }, [watchRearingPhasesType])

  async function fetchDlgFeed() {
    try {
      const response = await CO2Service.getClimaChDlgFeed(
        id,
        {
          heiferRaisingMethodId: watchHeiferRaisingMethod,
          firstCalvingAge: watchFirstCalvingAge?.toString()?.replace(',', '.'),
          youngCattleWeightBeforeCalving: watchYoungCattleWeightBeforeCalving
        }
      )
      replace(response.data)
    } catch (error) {
      handleErrors(error)
    }
  }

  function handleOpenIndividualModal() {
    const individualDefaultPhases = individualPhases(parseFloat(watchFirstCalvingAge))
    replace(individualDefaultPhases)
    setIsOpenIndPhasesModal(true)
  }

  function calculatePhasePeriod(previousPhase, currentPhase) {
    const ekaPhase = currentPhase.fixed
    const lastMonthPreviousPhase = previousPhase.period?.split('-')[1]
    const startMonthCurrentPhase = parseInt(lastMonthPreviousPhase) + 1
    const lastMonthCurrentPhase = ekaPhase ? 'EKA' : Math.floor(parseInt(currentPhase.days) / DAYS_IN_MONTH_LFL) + startMonthCurrentPhase

    return `${startMonthCurrentPhase}-${lastMonthCurrentPhase}`
  }

  function renderHeiferPhaseModal() {
    return <HeiferIndividualEdit
      isOpenIndPhasesModal={isOpenIndPhasesModal}
      rearingPhases={fields}
      handleClose={() => setIsOpenIndPhasesModal(false)}
      handleSubmit={(rearingPhases) => {
        rearingPhases.forEach((rearingPhase, index) => {
          rearingPhase.order = index + 1
          if (index !== 0) {
            rearingPhase.period = calculatePhasePeriod(rearingPhases[index - 1], rearingPhases[index])
          }
        })
        replace(rearingPhases)
        setIsOpenIndPhasesModal(false)
      }}
    />
  }

  function heiferTableFooter() {
    return <div style={{ display: 'flex', justifyContent: 'end', marginTop: '-1em' }}>
      <Button
        content={'Aufzuchtphasen bearbeiten'}
        onClick={() => setIsOpenIndPhasesModal(true)}
        type='button'
        style={{ color: 'white' }}
      />
    </div>
  }

  const heiferFilteredPhaseTypes = parseFloat(watchFirstCalvingAge) < 24 || parseFloat(watchFirstCalvingAge) > 30 ?
    [{ value: heiferPhaseTypes[0].key, text: heiferPhaseTypes[0].label }] :
    heiferPhaseTypes.map(heiferOption => ({ value: heiferOption.key, text: heiferOption.label }))

  return <>
    {renderHeiferPhaseModal()}
    <Form.Group style={{ gap: 50, alignItems: 'center' }}>
      <EditableLabelInput
        key={'farse'}
        label={'Färsen'}
        value={formatNumber(watchCalvesNumber, 0)}
        unit={'Tiere'}
        disabled
      />
      <FormFields.Select
        control={control}
        selectOnBlur={false}
        onChange={(_, { value }) => {
          clearErrors('rearingPhasesType')
          setValue('rearingPhasesType', value)
          if (value === 'individual') {
            handleOpenIndividualModal()
          } else if (value === 'dlg') {
            const dlgPhases = heiferFeedingPhases(parseFloat(watchFirstCalvingAge))
            replace(dlgPhases)
          }
        }}
        placeholder={'Bitte auswählen'}
        options={heiferFilteredPhaseTypes}
        name={'rearingPhasesType'}
        label={<div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: '0.5em' }}>
          <label>{'Wie möchten Sie Ihre Färsenfütterung erfassen?'}</label>
          <InfoPopup
            content={'Wenn Sie “angelehnt an die DLG-Fütterungsempfehlung” wählen, werden die DLG Standardwerte für das zuvor gewählte Produktionsverfahren standardmäßig vorausgefüllt sein.'}
            styleIcon={{ fontSize: '1.2em' }}
            position={'top center'}
          />
        </div>}
        withoutAsterisk
        noErrorMessage
        rules={{
          required: true
        }}
      />
      <Form.Field >
        <label style={{ marginBottom: '1em' }}>{'Welche Futterbauverfahren betreiben Sie auf dem Betrieb?'}</label>
        <Form.Group>
          {[...milkOptions?.silageTypes, { label: 'Kein Futterbau', id: NO_FUTTERBAU_ID }].map((option, index) => {
            return <FormFields.Checkbox
              name={'silageTypes'}
              id={option.id}
              checkboxValue={option.id}
              disabled={watchGrowSilage.find(silageId => silageId === NO_FUTTERBAU_ID) && index !== 2}
              checked={(watchGrowSilage || []).find(silageId => silageId === option.id)}
              onChange={(_, data) => {
                if (data.checked) {
                  if (index === 2) {
                    setValue('silageTypes', [option.id])
                    return
                  }
                  setValue('silageTypes', [...watchGrowSilage, option.id])
                  return
                }
                setValue('silageTypes', watchGrowSilage?.filter(silage => silage !== option.id))
              }}
              label={option.label}
              control={control}
            />
          })}
        </Form.Group>
      </Form.Field>
    </Form.Group>
    {fields.length > 0 && <>
      {Boolean(watchRearingPhasesType) && <HeiferPhasesTable />}
      {Boolean(watchRearingPhasesType) && watchRearingPhasesType === 'individual' && heiferTableFooter()}
    </>
    }
  </>
}
export default HeiferPhases